import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, MessageCircle, Mail, Check } from "lucide-react";

/**
 * Tasteful "send this to someone" card for resource pages.
 *
 * Tries the Web Share API first (native iOS/Android sheet — best UX), then
 * falls back to discrete buttons: copy link, iMessage/SMS, email. Copy is
 * intentionally non-alarming so it never outs the sender as a survivor.
 */
export default function ShareCard({ title, url, slug }) {
  const [copied, setCopied] = useState(false);

  const SHARE_MESSAGE =
    "This explained something I've been trying to name. Thought you might find it useful.";
  const subject = `Sister Signal — ${title}`;
  const fullText = `${SHARE_MESSAGE}\n\n${url}`;

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: subject, text: SHARE_MESSAGE, url });
      } catch {
        // user cancelled — no-op
      }
    } else {
      // Desktop browsers without Web Share API → copy as the primary action
      doCopy();
    }
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Link copied — paste it wherever feels right.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy manually.");
    }
  };

  // sms: links open Messages on iOS; on desktop they're inert which is fine.
  const smsHref = `sms:?&body=${encodeURIComponent(fullText)}`;
  const mailHref =
    `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullText)}`;

  return (
    <section
      className="mt-10 rounded-3xl bg-card border border-brand/20 p-7"
      data-testid={`share-card-${slug || "generic"}`}
    >
      <p className="font-script text-xl text-brand">if this helped, send it</p>
      <h3 className="mt-1 font-serif text-2xl leading-snug">
        Someone you love might be looking for these words too.
      </h3>
      <p className="mt-3 text-foreground/75 leading-relaxed">
        Sister Signal is private to use and quiet to share. The message below
        is gentle on purpose — it doesn't out you, it just opens a door.
      </p>

      <div className="mt-5 rounded-2xl bg-brand-soft/40 border border-brand/15 p-5">
        <p className="text-xs uppercase tracking-widest text-foreground/55">Suggested message</p>
        <p className="mt-1 font-serif italic text-foreground/85">"{SHARE_MESSAGE}"</p>
        <p className="mt-2 text-xs text-foreground/55 break-all">{url}</p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          onClick={nativeShare}
          data-testid="share-native"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white text-sm hover:bg-brand/90 transition-all"
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
        <button
          onClick={doCopy}
          data-testid="share-copy"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-card border border-border text-sm hover:border-brand/40 transition-all"
        >
          {copied ? <Check className="w-4 h-4 text-brand" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied" : "Copy link"}
        </button>
        <a
          href={smsHref}
          data-testid="share-sms"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-card border border-border text-sm hover:border-brand/40 transition-all"
        >
          <MessageCircle className="w-4 h-4" /> Text
        </a>
        <a
          href={mailHref}
          data-testid="share-email"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-card border border-border text-sm hover:border-brand/40 transition-all"
        >
          <Mail className="w-4 h-4" /> Email
        </a>
      </div>
    </section>
  );
}
